import { isValidElement, memo, useEffect, useState } from 'react';

import { css, styled } from '@mui/material';
import { useTranslation } from '@kc-console/core';
import {
  IcChevronDownSmallRegular,
  IcChevronRightSmallRegular,
  IcRefreshRegular,
} from '@kep/kcds-icons';

import { IconButtonGhostMini, iconButtonGhostMiniClasses } from '../IconButtonGhostMini';
import { InfoButton } from '../InfoButton';
import { labelClasses } from './theme';

import type { ReactElement } from 'react';
import type { Theme } from '@mui/material';
import type { IconButtonGhostMiniProps } from '../IconButtonGhostMini';
import type { InfoButtonProps } from '../InfoButton';
import type { LabelOwnerState, LabelProps, LabelSize } from './types';

function defaultRenderNumberIndex(numberIndex: number) {
  return numberIndex.toLocaleString();
}

const labelRefetchButtonAnimatingClass = 'LabelRefetchButton-animating';
const labelRefetchButtonIconClass = 'LabelRefetchButton-icon';

interface LabelRefetchButtonProps extends IconButtonGhostMiniProps {
  isFetching?: boolean;
}

const LabelRefetchButton = memo(function LabelRefetchButton(props: LabelRefetchButtonProps) {
  const { isFetching, onClick, ...others } = props;
  const [animationCount, setAnimationCount] = useState(0);
  const isAnimating = animationCount > 0;

  useEffect(() => {
    if (isFetching) {
      setAnimationCount(1);
    }
  }, [isFetching]);

  const handleAnimationEnd = () => {
    if (isFetching) {
      setAnimationCount(prev => prev + 1);
    } else {
      setAnimationCount(0);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isFetching) return;
    onClick?.(e);
  };

  return (
    <LabelRefetchButtonRoot
      className={isAnimating ? labelRefetchButtonAnimatingClass : undefined}
      onClick={handleClick}
      onAnimationEnd={handleAnimationEnd}
      style={{ '--animation-count': animationCount } as React.CSSProperties}
      {...others}
    >
      <IcRefreshRegular className={labelRefetchButtonIconClass} />
    </LabelRefetchButtonRoot>
  );
});

const LabelRefetchButtonRoot = styled(IconButtonGhostMini)(
  () => css`
    @keyframes labelRefetchRotate {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }

    &.${labelRefetchButtonAnimatingClass} .${labelRefetchButtonIconClass} {
      animation: labelRefetchRotate 1s linear var(--animation-count) reverse;
    }
  `,
);

const Label = (props: LabelProps) => {
  const { t } = useTranslation();
  const {
    label,
    ref,
    renderNumberIndex = defaultRenderNumberIndex,
    required = false,
    description,
    id,
    numberIndex,
    className,
    style,
    htmlFor,
    info,
    refetch,
    expandable,
    showZeroNumberIndex = false,
    size = 'sm', // theme defaultProps에서 이동
    textVariant = 'standalone', // theme defaultProps에서 이동
    disabled = false, // theme defaultProps에서 이동
    type = 'primary', // theme defaultProps에서 이동
  } = props;

  const { onRefetch, isRefetching = false } = refetch ?? {};

  const {
    open = false,
    onClick,
    disabled: expandableDisabled = false,
    ...others
  } = expandable ?? {};
  const isExpandableDisabled = disabled || expandableDisabled;
  const expandableOnClick = isExpandableDisabled ? undefined : onClick;

  const ownerState: LabelOwnerState = {
    ...props,
    size,
    textVariant,
    disabled,
    type,
  };

  const infoButton = () => {
    if (isValidElement(info) && info.type === InfoButton) {
      const element = info as ReactElement<InfoButtonProps>;
      const { size: infoButtonSize, disabled: infoButtonDisabled } = element.props;
      const defaultInfoButtonSize = size === 'sm' ? 'sm' : 'md';

      return (
        <InfoButton
          {...element.props}
          size={infoButtonSize ?? defaultInfoButtonSize}
          disabled={infoButtonDisabled ?? disabled}
        />
      );
    } else if (typeof info === 'string') {
      const defaultInfoButtonSize = size === 'sm' ? 'sm' : 'md';
      return (
        <InfoButton
          alertType="tooltip"
          tooltipProps={{ message: info }}
          size={defaultInfoButtonSize}
          disabled={disabled}
        />
      );
    }

    return null;
  };

  const refetchButton = (
    <LabelRefetchButton
      type="circle"
      size={size === 'sm' ? 'sm' : 'md'}
      disabled={disabled}
      isFetching={isRefetching}
      onClick={onRefetch}
    />
  );

  const getExpandableIconButtonSize = (size: LabelSize) => {
    switch (size) {
      case 'sm':
      case 'md':
        return 'sm';
      case 'lg':
      case 'xl':
        return 'md';
      default:
        return 'sm';
    }
  };

  return (
    <LabelRoot ref={ref} className={className} style={style} ownerState={ownerState}>
      <LabelContainer ownerState={ownerState}>
        <LabelClickableArea ownerState={ownerState} onClick={expandableOnClick}>
          {expandable && (
            <LabelExpandable
              disabled={isExpandableDisabled}
              size={getExpandableIconButtonSize(size)}
              ownerState={ownerState}
              aria-expanded={open}
              aria-label={open ? t('ui:action.collapse') : t('ui:action.expand')}
              {...others}
            >
              {open ? <IcChevronDownSmallRegular /> : <IcChevronRightSmallRegular />}
            </LabelExpandable>
          )}
          <LabelContent
            as={'label'}
            htmlFor={htmlFor}
            id={id}
            ownerState={ownerState}
            style={{ cursor: expandableOnClick ? 'pointer' : 'default' }}
          >
            {label}
          </LabelContent>
          {required && <LabelRequiredMark ownerState={ownerState}>{`*`}</LabelRequiredMark>}
        </LabelClickableArea>

        {numberIndex != null && (showZeroNumberIndex || numberIndex !== 0) && (
          <LabelNumberIndex ownerState={ownerState}>
            {renderNumberIndex(numberIndex)}
          </LabelNumberIndex>
        )}
        {info && <LabelInfo ownerState={ownerState}>{infoButton()}</LabelInfo>}
        {refetch && <LabelRefetch ownerState={ownerState}>{refetchButton}</LabelRefetch>}
      </LabelContainer>
      {description && size !== 'sm' && (
        <LabelDescription ownerState={ownerState}>{description}</LabelDescription>
      )}
    </LabelRoot>
  );
};

export default Label;

// Styled Components
const LabelRoot = styled('div', {
  target: labelClasses.root,
})<Pick<LabelProps, 'ref' | 'className' | 'style'> & { ownerState: LabelOwnerState }>(
  ({ ownerState }) => css`
    display: ${ownerState.textVariant === 'inline' ? 'inline-flex' : 'flex'};
    flex-direction: column;
    row-gap: 2px;
  `,
);

const LabelContainer = styled('div', {
  target: labelClasses.container,
})<{ ownerState: LabelOwnerState }>(({ ownerState, theme }) => {
  const { size, type, disabled } = ownerState;
  return css`
    display: flex;
    column-gap: 4px;
    align-items: center;

    ${getSizeContainerStyles(size)}
    ${getTypeStyles(type, disabled, theme)}
  `;
});

const LabelContent = styled('label', {
  target: labelClasses.label,
})<{ ownerState: LabelOwnerState }>(({ ownerState, theme }) => {
  const { size } = ownerState;
  return css`
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;

    ${getSizeLabelStyles(size, theme)}
  `;
});

const LabelRequiredMark = styled('div', {
  target: labelClasses.requireMark,
})<{ ownerState: LabelOwnerState }>(({ ownerState, theme }) => {
  const { size } = ownerState;
  return css`
    font-weight: 400;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;

    ${getSizeRequireMarkStyles(size, theme)}
  `;
});

const LabelNumberIndex = styled('div', {
  target: labelClasses.numberIndex,
})<{ ownerState: LabelOwnerState }>(({ ownerState, theme }) => {
  const { size } = ownerState;
  return css`
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;

    ${getSizeNumberIndexStyles(size, theme)}
  `;
});

const LabelInfo = styled('div', {
  target: labelClasses.info,
})<{ ownerState: LabelOwnerState }>`
  display: flex;
`;

const LabelRefetch = styled('div', {
  target: labelClasses.refetch,
})<{ ownerState: LabelOwnerState }>`
  display: flex;
`;

const LabelDescription = styled('div', {
  target: labelClasses.description,
})<{ ownerState: LabelOwnerState }>(({ ownerState, theme }) => {
  const { size, disabled } = ownerState;
  return css`
    color: ${theme.color['text/weaker/default']};
    font-weight: 400;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
    word-break: break-all;

    ${getSizeDescriptionStyles(size, theme)}
    ${disabled && getDisabledStyles(theme)}
  `;
});

const LabelClickableArea = styled('div', {
  target: labelClasses.clickableArea,
})<{ ownerState: LabelOwnerState }>`
  display: flex;
  align-items: center;
  column-gap: 4px;
`;

const LabelExpandable = styled(IconButtonGhostMini, {
  target: labelClasses.expandable,
})<{ ownerState: LabelOwnerState }>``;

// Style helper functions
const getSizeContainerStyles = (size: LabelOwnerState['size']) => {
  const sizeStylesMap = {
    sm: css`
      min-height: 20px;
    `,
    md: css`
      min-height: 21px;
    `,
    lg: css`
      min-height: 24px;
    `,
    xl: css`
      min-height: 27px;
    `,
  };
  return sizeStylesMap[size] || css``;
};

const getSizeLabelStyles = (size: LabelOwnerState['size'], theme: Theme) => {
  const sizeStylesMap = {
    sm: css`
      ${theme.typography['label/regular/sm']}
    `,
    md: css`
      ${theme.typography['label/medium/md']}
    `,
    lg: css`
      ${theme.typography['label/semibold/lg']}
    `,
    xl: css`
      ${theme.typography['label/semibold/xl']}
    `,
  };
  return sizeStylesMap[size] || css``;
};

const getSizeRequireMarkStyles = (size: LabelOwnerState['size'], theme: Theme) => {
  const sizeStylesMap = {
    sm: css`
      ${theme.typography['label/regular/sm']}
    `,
    md: css`
      ${theme.typography['label/semibold/md']}
    `,
    lg: css`
      ${theme.typography['label/bold/lg']}
    `,
    xl: css`
      ${theme.typography['label/bold/lg']}
    `,
  };
  return sizeStylesMap[size] || css``;
};

const getSizeNumberIndexStyles = (size: LabelOwnerState['size'], theme: Theme) => {
  const sizeStylesMap = {
    sm: css`
      ${theme.typography['label/regular/sm']}
    `,
    md: css`
      ${theme.typography['label/semibold/md']}
    `,
    lg: css`
      ${theme.typography['label/medium/lg']}
    `,
    xl: css`
      ${theme.typography['label/medium/lg']}
    `,
  };
  return sizeStylesMap[size] || css``;
};

const getSizeDescriptionStyles = (size: LabelOwnerState['size'], theme: Theme) => {
  const sizeStylesMap = {
    sm: css``, // sm에서는 description이 표시되지 않음
    md: css`
      ${theme.typography['label/regular/xs']}
    `,
    lg: css`
      ${theme.typography['label/regular/sm']}
    `,
    xl: css`
      ${theme.typography['label/regular/sm']}
    `,
  };
  return sizeStylesMap[size] || css``;
};

const getTypeStyles = (
  type: LabelOwnerState['type'],
  disabled: LabelOwnerState['disabled'],
  theme: Theme,
) => {
  if (disabled) {
    return css`
      & .${labelClasses.label} {
        color: ${theme.color['text/disabled']};
      }

      & .${labelClasses.requireMark} {
        color: ${theme.color['text/disabled']};
      }

      & .${iconButtonGhostMiniClasses.root} {
        color: ${theme.color['icon/disabled']};
      }

      & .${labelClasses.numberIndex} {
        color: ${theme.color['text/disabled']};
      }
    `;
  }

  const typeStylesMap = {
    primary: css`
      & .${labelClasses.label} {
        color: ${theme.color['text/default']};
      }

      & .${labelClasses.requireMark} {
        color: ${theme.color['text/default']};
      }

      & .${iconButtonGhostMiniClasses.root} {
        color: ${theme.color['icon/weaker/default']};
      }

      & .${labelClasses.numberIndex} {
        color: ${theme.color['text/weaker/default']};
      }
    `,
    secondary: css`
      & .${labelClasses.label} {
        color: ${theme.color['text/weaker/default']};
      }

      & .${labelClasses.requireMark} {
        color: ${theme.color['text/weaker/default']};
      }

      & .${iconButtonGhostMiniClasses.root} {
        color: ${theme.color['icon/weaker/default']};
      }

      & .${labelClasses.numberIndex} {
        color: ${theme.color['text/weaker/default']};
      }
    `,
  };
  return typeStylesMap[type] || css``;
};

const getDisabledStyles = (theme: Theme) => css`
  color: ${theme.color['text/disabled']};
`;
